import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { groupsAPI } from '../../services/api';

interface Group {
  id: number;
  name: string;
  noofusers: number;
  isprice: number | boolean;
  status: number | boolean;
}

interface GroupsState {
  groups: Group[];
  loading: boolean;
  error: string | null;
  statistics: {
    active: number;
    deactive: number;
    total: number;
  };
  statisticsLoading: boolean;
  filters: {
    search: string;
    status: string;
  };
  pagination: {
  page: number;
  items_per_page: number;
    total: number;
  };
}

const initialState: GroupsState = {
  groups: [],
  loading: false,
  error: null,
  statistics: {
    active: 0,
    deactive: 0,
    total: 0,
  },
  statisticsLoading: false,
  filters: {
    search: '',
    status: '',
  },
  pagination: {
    page: 1,
    items_per_page: 10,
    total: 0,
  },
};

export const fetchGroups = createAsyncThunk(
  'groups/fetchGroups',
  async (params: any) => {
    try {
      const response = await groupsAPI.getGroups(params);

      // Normalize response for list + pagination
      let groups: any[] = [];
      let pagination = { page: 1, items_per_page: 10, total: 0 };

      if (response.data) {
        // New style: { data: [...], payload: { page, items_per_page, total } }
        if (Array.isArray(response.data.data)) {
          groups = response.data.data;
          const payload = response.data.payload || {};
          pagination = {
            page: payload.page || 1,
            items_per_page: payload.items_per_page || payload.limit || groups.length,
            total: payload.total || groups.length,
          };
        } else if (Array.isArray(response.data)) {
          groups = response.data;
          pagination = { page: 1, items_per_page: response.data.length, total: response.data.length };
        } else if (Array.isArray(response.data.groups)) {
          groups = response.data.groups;
          pagination = { page: 1, items_per_page: groups.length, total: groups.length };
        }
      }

      return { items: groups, pagination };
    } catch (error) {
      throw error;
    }
  }
);

export const fetchGroupStatistics = createAsyncThunk(
  'groups/fetchGroupStatistics',
  async () => {
    try {
      // Call all three statistics APIs in parallel
      const [activeResponse, deactiveResponse, totalResponse] = await Promise.all([
        groupsAPI.getActiveGroups(),
        groupsAPI.getDeactiveGroups(),
        groupsAPI.getTotalGroups()
      ]);

      // Extract data from responses (handle different response structures)
      const getCount = (response: any, type: string) => {
        if (Array.isArray(response.data)) {
          return response.data.length;
        } else if (response.data && Array.isArray(response.data.data)) {
          return response.data.data.length;
        } else if (response.data && Array.isArray(response.data.groups)) {
          return response.data.groups.length;
        }
        return 0;
      };

      const activeCount = getCount(activeResponse, 'Active');
      const deactiveCount = getCount(deactiveResponse, 'Deactive');
      const totalCount = getCount(totalResponse, 'Total');

      const result = {
        active: activeCount,
        deactive: deactiveCount,
        total: totalCount,
      };

      return result;
    } catch (error) {
      throw error;
    }
  }
);

export const addGroup = createAsyncThunk(
  'groups/addGroup',
  async (groupData: any) => {
    try {
      const response = await groupsAPI.addGroup(groupData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

export const updateGroup = createAsyncThunk(
  'groups/updateGroup',
  async ({ id, data }: { id: number; data: any }) => {
    try {
      const response = await groupsAPI.updateGroup(id, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

export const deleteGroup = createAsyncThunk(
  'groups/deleteGroup',
  async (id: number) => {
    try {
      await groupsAPI.deleteGroup(id);
      return id;
    } catch (error) {
      throw error;
    }
  }
);

const groupsSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload.items;
        // action.payload.pagination has { page, items_per_page, total }
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch groups';
      })
      .addCase(fetchGroupStatistics.pending, (state) => {
        state.statisticsLoading = true;
      })
      .addCase(fetchGroupStatistics.fulfilled, (state, action) => {
        state.statisticsLoading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchGroupStatistics.rejected, (state) => {
        state.statisticsLoading = false;
      })
      .addCase(addGroup.fulfilled, (state, action) => {
        // Refresh groups list after adding
        state.groups.unshift(action.payload);
        state.pagination.total += 1;
      })
      .addCase(updateGroup.fulfilled, (state, action) => {
        // Update the group in the list
        const index = state.groups.findIndex(group => group.id === action.payload.id);
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
      })
      .addCase(deleteGroup.fulfilled, (state, action) => {
        // Remove the group from the list
        state.groups = state.groups.filter(group => group.id !== action.payload);
        state.pagination.total -= 1;
      });
  },
});

export default groupsSlice.reducer; 